/**
 * 销售代传录音 · 纯逻辑（不碰 React / 网络实现，便于单测）：文件校验、分块计划、
 * 顺序上传（逐块重试 / 可取消）、状态展示映射、是否要轮询。
 *
 * 为什么顺序上传而不是并发：这里是桌面浏览器、办公网络，一块 4MB 一两秒就传完，顺序最简单，
 * 也让「第 k 块之后服务端一定收齐了 0..k」成立，complete 时不会出现缺块。
 */
import type { AudioImportDto, AudioImportSpeakerDto, AudioImportStatus } from '../../../api/advisor';

/** 与 content-pipeline 的白名单一致（只做提示，真正把关是服务端 ffmpeg 解码） */
export const ALLOWED_EXTENSIONS = ['mp3', 'm4a', 'aac', 'wav', 'ogg', 'amr', 'flac'] as const;
export const MAX_FILE_BYTES = 200 * 1024 * 1024;
/** 处理中的导入多久刷新一次 */
export const POLL_INTERVAL_MS = 4000;
/** 单块失败最多再试几次（共 1 + 3 次） */
export const CHUNK_RETRIES = 3;

export const extensionOf = (name: string): string => {
  const i = name.lastIndexOf('.');
  return i < 0 ? '' : name.slice(i + 1).toLowerCase();
};

export type FileCheck = { ok: true } | { ok: false; message: string };

export function validateFile(file: { name: string; size: number }): FileCheck {
  if (!ALLOWED_EXTENSIONS.includes(extensionOf(file.name) as (typeof ALLOWED_EXTENSIONS)[number])) {
    return { ok: false, message: `暂时只支持 ${ALLOWED_EXTENSIONS.join('、')} 格式的录音` };
  }
  if (file.size <= 0) return { ok: false, message: '这个文件是空的，请换一个' };
  if (file.size > MAX_FILE_BYTES) return { ok: false, message: '文件太大了（最大 200MB），请先剪短再上传' };
  return { ok: true };
}

export interface ChunkSpec {
  index: number;
  /** [start, end) 字节区间 */
  start: number;
  end: number;
}

export function planChunks(sizeBytes: number, chunkSize: number): ChunkSpec[] {
  if (!Number.isFinite(sizeBytes) || sizeBytes <= 0 || !Number.isFinite(chunkSize) || chunkSize <= 0) return [];
  const out: ChunkSpec[] = [];
  for (let start = 0, index = 0; start < sizeBytes; start += chunkSize, index += 1) {
    out.push({ index, start, end: Math.min(start + chunkSize, sizeBytes) });
  }
  return out;
}

export class UploadCancelledError extends Error {
  constructor() {
    super('已取消上传');
    this.name = 'UploadCancelledError';
  }
}

/** 值得重试的失败：网络错误（没有响应）、5xx、超时 / 限流；4xx 业务错误（格式不对、状态不对）重试没用 */
export function isRetryableError(err: unknown): boolean {
  const status = (err as { response?: { status?: number } } | null)?.response?.status;
  if (status === undefined) return true;
  return status >= 500 || status === 408 || status === 429;
}

const backoffMs = (attempt: number) => 500 * 3 ** attempt; // 500 / 1500 / 4500

export interface UploadChunksOptions {
  file: { size: number; slice: (start: number, end: number) => Blob };
  chunkSize: number;
  totalChunks: number;
  send: (index: number, chunk: Blob, signal?: AbortSignal) => Promise<unknown>;
  /** 0~100 */
  onProgress?: (pct: number) => void;
  signal?: AbortSignal;
  retries?: number;
  sleep?: (ms: number) => Promise<void>;
}

/**
 * 按序上传所有块。每块失败按退避重试；取消（signal）立刻停止并抛 UploadCancelledError；
 * 不可重试的错误（4xx）原样抛出。块数与服务端给的 totalChunks 对不上说明文件变了，直接报错而不是上传一半。
 */
export async function uploadChunks(opts: UploadChunksOptions): Promise<void> {
  const { file, chunkSize, totalChunks, send, onProgress, signal } = opts;
  const retries = opts.retries ?? CHUNK_RETRIES;
  const sleep = opts.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  const plan = planChunks(file.size, chunkSize);
  if (plan.length !== totalChunks) {
    throw new Error('文件大小与服务端记录对不上，请重新选择文件上传');
  }

  const throwIfCancelled = () => {
    if (signal?.aborted) throw new UploadCancelledError();
  };

  for (const spec of plan) {
    throwIfCancelled();
    const blob = file.slice(spec.start, spec.end);
    for (let attempt = 0; ; attempt += 1) {
      try {
        await send(spec.index, blob, signal);
        break;
      } catch (err) {
        if (signal?.aborted) throw new UploadCancelledError();
        if (attempt >= retries || !isRetryableError(err)) throw err;
        await sleep(backoffMs(attempt));
        throwIfCancelled();
      }
    }
    onProgress?.(Math.round(((spec.index + 1) / plan.length) * 100));
  }
}

// ── 状态展示 ──────────────────────────────────────────────────────────────

export interface ImportBadge {
  label: string;
  /** antd Tag 颜色 */
  color: string;
  /** 一句说明（给销售看，人话） */
  hint: string;
}

const ACTIVE: ReadonlySet<AudioImportStatus> = new Set(['transcribing', 'ingesting']);

/** 还在后台处理中（需要轮询）。uploading 不算：那是浏览器自己在传，或上传中途断了，轮询没有意义。 */
export function needsPolling(list: Pick<AudioImportDto, 'status'>[] | undefined): boolean {
  return !!list?.some((v) => ACTIVE.has(v.status));
}

export function describeImport(v: AudioImportDto): ImportBadge {
  switch (v.status) {
    case 'uploading':
      return { label: '上传未完成', color: 'orange', hint: '上传没有传完，请删除后重新上传' };
    case 'transcribing':
      return { label: '识别中', color: 'processing', hint: '正在把录音转成文字，通常几十秒到几分钟' };
    case 'awaiting_speaker':
      return {
        label: '待选讲述人',
        color: 'gold',
        hint:
          v.attribution === 'llm'
            ? '请点「选讲述人」，对照样例句选出哪位是讲述人（这次识别可能不准，请核实）'
            : '请点「选讲述人」，选出哪位是讲述人；只有讲述人的话会进书',
      };
    case 'ingesting':
      return { label: '整理中', color: 'processing', hint: '正在把讲述人的话整理成素材' };
    case 'done':
      return { label: '已完成', color: 'success', hint: v.chapterHint || '已整理进这本书' };
    case 'failed':
      return {
        label: '失败',
        color: 'error',
        hint: v.error || '处理失败了',
      };
  }
}

export const MODE_LABEL: Record<AudioImportDto['mode'], string> = {
  solo: '只有讲述人',
  interview: '采访录音',
};

export function formatDuration(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return '-';
  const total = Math.round(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return m > 0 ? `${m} 分 ${s} 秒` : `${s} 秒`;
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function speakerSummary(s: AudioImportSpeakerDto): string {
  return `${s.label} · 发言占 ${s.sharePct}% · 约 ${formatDuration(s.talkMs)}`;
}
